import { useCallback, useState, type SubmitEvent } from "react";
import { catchError, of, tap } from "rxjs";

import type { APILibraryCategoryResponseType } from "@/core/data_sources/models/APILibraryCategory";
import { Button, TextInput } from "@/core/ui/atoms";
import { DrawerTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { LibraryCategoriesStore } from "../data_sources/store";

export function EditCategoryDrawer({
  category,
  onClose,
  onSaved,
}: {
  readonly category: APILibraryCategoryResponseType;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { actions } = useStore(LibraryCategoriesStore.instance);
  const [error, setError] = useState("");
  const [name, setName] = useState(category.name ?? "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!category.id || !name.trim()) {
        setError("Name is required.");
        return;
      }

      setSaving(true);
      setError("");
      actions
        .updateCategory(String(category.id), { name: name.trim() })
        .pipe(
          tap(() => {
            onSaved();
          }),
          catchError((requestError) => {
            setError(
              requestError instanceof ErrorResponse
                ? requestError.error.message
                : requestError instanceof Error
                  ? requestError.message
                  : "Unable to save category.",
            );
            return of(null);
          }),
          tap(() => setSaving(false)),
        )
        .subscribe();
    },
    [actions, category.id, name, onSaved],
  );

  return (
    <DrawerTemplate
      content={
        <form id="edit-category-form" onSubmit={handleSubmit}>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">
              Name
            </span>
            <TextInput
              onChange={(event) => setName(event.target.value)}
              value={name}
            />
          </label>
          {error ? <p className="mt-3 text-xs text-critical">{error}</p> : null}
        </form>
      }
      footer={
        <>
          <Button disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={saving}
            form="edit-category-form"
            type="submit"
            variant="primary"
          >
            {saving ? "Saving..." : "Save category"}
          </Button>
        </>
      }
      header={
        <div>
          <h2 className="text-base font-semibold text-ink">Edit category</h2>
          <p className="mt-0.5 text-xs text-ink-subtle">{category.name}</p>
        </div>
      }
    />
  );
}
